import { isBrowserOffline } from '../../utils/browserOnline';
import { isUnreadable } from '../analytics/utils/statValue';

/**
 * The four things a gated React Query read can be, as far as the UI is
 * concerned.
 *
 * `never-issued` is the one the dashboard keeps getting wrong, and it is the
 * reason this type exists rather than a pair of booleans at each call site.
 * React Query v4 reports a DISABLED query as `isLoading: true` with
 * `fetchStatus: 'idle'` (measured against a real `QueryClient` in
 * `analytics-read-error-state.test.tsx`, "the premise"), so every surface that
 * drives its loading affordance from the raw `isLoading` shows that affordance
 * FOREVER whenever its gate is unsatisfied — and every surface that drives a
 * definitive answer from `data ?? <empty>` states that answer about a question
 * nobody asked.
 *
 * Both halves of that have now been found in production code three runs
 * running: a real `0` in seven analytics cards (run 97), a series with no
 * points in four charts (run 99), a `--` that never resolves (run 100), and —
 * this run — a navbar spinner on every page and a home-screen panel that
 * claimed the organization had no notifications.
 */
export type ReadState = 'unreadable' | 'loading' | 'never-issued' | 'answered';

/**
 * Classify a React Query result into the four states above.
 *
 * Takes the query's OWN flags rather than a restatement of its `enabled:`
 * expression. That is deliberate and it is the correction D62 asked for:
 * `isReadPending(isEnabled, …)` requires every caller to re-derive the gate in
 * a second file, and a caller that re-derives it incompletely (a hook gated on
 * `!!organizationId && !!sport` called with `Boolean(organizationId)`) is wrong
 * in the direction that spins forever, with nothing to catch it. `data` and
 * `isInitialLoading` are facts the query already knows about itself, so no
 * caller can restate them wrongly.
 *
 * Order matters, and it is the order the campaign has used since run 97:
 *
 * 1. `unreadable` FIRST — a refused read with nothing to show is not loading
 *    and is not an answer. Delegated to the shared `isUnreadable`, whose `!data`
 *    half keeps a correct value on screen when a BACKGROUND refetch fails
 *    (R96-3); a card that read `isError` alone would blank a good number
 *    because a 30-second refresh failed, which is the mirror of the defect.
 * 2. `loading` — `isInitialLoading` is `isLoading && isFetching`, so it is
 *    false for a disabled query. This is the flag the raw `isLoading` should
 *    always have been.
 * 3. `never-issued` — not failed, not in flight, and nothing to show. The read
 *    was never sent, so the honest thing to render is a prompt for whatever is
 *    missing, never an empty state and never a zero. **What is missing is not
 *    always the gate**: a query the browser paused for want of a network lands
 *    here too, and correctly so (nothing was sent). Which sentence to show is
 *    `scopePromptI18nKey`'s decision, not this function's — see it for why the
 *    distinction lives there and not in a fifth member of this type (R134-4).
 * 4. `answered` — the read resolved. A resolved read with zero rows is an
 *    answer, and the caller's empty state is correct there and ONLY there.
 *
 * Note what `answered` does NOT distinguish: zero rows from many. That stays
 * with the caller, because "no notifications yet" and "no users found" are
 * statements about the caller's domain, not about the read.
 */
export function classifyRead(
    isError: boolean,
    isInitialLoading: boolean,
    data: unknown,
): ReadState {
    if (isUnreadable(isError, data)) return 'unreadable';
    if (isInitialLoading) return 'loading';
    if (data === undefined || data === null) return 'never-issued';
    return 'answered';
}

/**
 * The shape `classifyMultiRead` reads off one member of a `useQueries` result
 * array. Structural on purpose: React Query's own `UseQueryResult` is a large
 * discriminated union, and every field beyond these three is irrelevant here.
 */
export interface MultiReadMember {
    isError: boolean;
    isInitialLoading: boolean;
    data: unknown;
}

/**
 * Classify a FAN-OUT read — `useQueries` over N ids — into one `ReadState`.
 *
 * Why this exists, and why it is here rather than at a call site. Every census
 * and gate this campaign has built is keyed on a call site that destructures
 * `{ data, isError }` from a hook returning ONE query result. `useQueries`
 * returns an ARRAY, so a fan-out read matches none of those matchers and is
 * outside every population by SHAPE rather than by judgement — D61@r97(a)
 * exactly: the scoping clause of a census is the one part of the instrument
 * nobody falsifies. Four hooks in this tree fan out
 * (`useGetCourtsCalendar`, `useGetCourtWeeklyCalendar`,
 * `useGetCoachWeeklyCalendar`, `useGetOrganizationCourts`), and when run 137
 * looked, one live consumer aggregated `isLoading` across the array and had no
 * `isError` term at all — so a refused calendar read rendered as every court at
 * 0% occupancy.
 *
 * The precedence is `classifyRead`'s, applied member-wise and then folded, and
 * the fold is deliberately PESSIMISTIC: these N queries answer ONE question, so
 * the aggregate is only as good as its worst member. A total computed over a
 * set where one member is missing is not a smaller total, it is a wrong one —
 * and it renders as a confident scalar with no trace of its own absence
 * (D61@r97(b)).
 *
 * `results.length === 0` is `never-issued` rather than `answered`: an empty
 * fan-out means no request was sent, which is what the gate on the id list was
 * for. A caller whose id list is legitimately empty because an ANSWERED read
 * returned no rows must say so itself — see `useCourtPulseData`, which does.
 */
export function classifyMultiRead(results: ReadonlyArray<MultiReadMember>): ReadState {
    if (results.length === 0) return 'never-issued';
    const states = results.map((result) =>
        classifyRead(result.isError, result.isInitialLoading, result.data),
    );
    if (states.includes('unreadable')) return 'unreadable';
    if (states.includes('loading')) return 'loading';
    if (states.includes('never-issued')) return 'never-issued';
    return 'answered';
}

/**
 * The i18n key for "Select an organization first".
 *
 * Borrowed from the `command_palette` namespace on purpose, and named here so
 * the borrowing is greppable from one place. `src/i18n/{en,fr}.ts` are inside
 * the open-PR fence and frozen for this campaign (R96-7), so a surface that
 * needs this sentence must reuse a key that already exists in BOTH catalogues
 * rather than add a neutral one. This key does: `en.ts` has "Select an
 * organization first", `fr.ts` has "Sélectionnez d'abord une organisation".
 *
 * When the freeze lifts (R96-7 / R100-8), move the string to a neutral
 * namespace and re-point this constant — one edit, and every caller follows.
 */
export const SELECT_ORGANIZATION_I18N_KEY = 'command_palette.select_org_first';

/**
 * The i18n key for "Please select a location first".
 *
 * The location-gated sibling of the constant above, and borrowed on the same
 * terms: `src/i18n/{en,fr}.ts` are inside the open-PR fence (R96-7), so a
 * surface that needs this sentence reuses a key that already exists in BOTH
 * catalogues rather than adding a neutral one. This key does: `en.ts` has
 * "Please select a location first", `fr.ts` has "Veuillez d'abord sélectionner
 * un club".
 *
 * It lives in the `event` namespace, which is a borrow rather than a home —
 * nothing about the sentence is event-specific, and the analytics charts that
 * now use it have no connection to events. Named here so the borrowing is
 * greppable from one place, and so that when the freeze lifts (R96-7 / R100-8)
 * moving the string to a neutral namespace is one edit.
 */
export const SELECT_LOCATION_I18N_KEY = 'event.select_location_first';

/**
 * The i18n key for a read that was issued and refused.
 *
 * Same fence, same reasoning as above: `common.unexpected_error` is generic,
 * already translated in both catalogues, and says the one true thing — that we
 * asked and did not get an answer. Deliberately NOT the shared `ErrorState`
 * component, whose title and Retry label are still hardcoded English (R100-8)
 * and whose 60px block is wrong inside a 340px navbar dropdown.
 */
export const READ_FAILED_I18N_KEY = 'common.unexpected_error';

/**
 * The i18n key for a read that was never issued because the BROWSER IS OFFLINE.
 *
 * Added run 135 (R134-4), and the first genuinely NEW key this campaign has
 * added rather than borrowed — the fence that forced the borrowing (R96-7) lifted
 * at run 133, and no existing string says this. Present in both catalogues.
 *
 * It is the second reason a read can be never-issued, and until this run the
 * dashboard had only ever rendered the first. See `scopePromptI18nKey`.
 */
export const OFFLINE_NO_DATA_I18N_KEY = 'common.offline_no_data';

/**
 * Which `enabled:` predicate a never-issued read is waiting on.
 *
 * Only two gates in this dashboard are reachably unsatisfied for a whole
 * session, and they are the two the campaign keeps finding: the selected
 * ORGANIZATION (62 gated hooks — a refused or empty `my-organizations` leaves
 * it undefined for every page, measured run 100) and the selected LOCATION
 * (15 — an org with no locations, or a locations read that failed). An
 * entity-id gate is a third shape and is not one of these: the honest render
 * there is a not-found page, not a prompt (R98-1).
 */
export type ReadGate = 'organization' | 'location';

/**
 * The prompt key for a gate, in ONE place.
 *
 * Three components now render this sentence — `ChartReadState` inside a chart
 * box, `ScopePrompt` inside a page body and `InlineScopePrompt` beside a single
 * control — plus a dozen inline `translate(scopePromptI18nKey(…))` call sites,
 * and the branch that picks the key is the part that must not be copied. D59: a
 * private copy of a shared decision is a fork, and the way this one would fail
 * is silent, because a component that borrowed the wrong key still renders a
 * real translated sentence.
 *
 * ---
 *
 * OFFLINE (R134-4, run 135). `never-issued` has TWO causes and this dashboard
 * had only ever rendered one of them.
 *
 * React Query v4 with the default `networkMode: 'online'` — which every query
 * here uses, because `src/queryClient.ts` sets no `defaultOptions` — does not
 * send a request while `onlineManager` reports no network. It PAUSES it
 * (`query.js:377`, `fetchStatus: 'paused'`). Such a read reports
 * `isLoading: true`, `isFetching: false`, `isInitialLoading: false`,
 * `isError: false` and `data: undefined`, which is byte-identical to a DISABLED
 * query on every flag `classifyRead` receives — measured against a real
 * `QueryClient` in `offline-read-state.test.tsx`, "the premise".
 *
 * So `classifyRead` calls it `never-issued`, and that is CORRECT: the request
 * genuinely was never issued. The defect was never the classification — it was
 * that this function assumed the only reason a read is never issued is a
 * missing scope, so ~20 surfaces told an operator who had already chosen a club
 * to choose a club, and pointed them at a navbar picker that cannot restore a
 * network.
 *
 * Fixing it HERE rather than in `classifyRead` is deliberate, and the reason is
 * the shape of the 62 surfaces that branch on a read state. They consume it as a
 * chain of `state === 'unreadable' / 'loading' / 'never-issued'` with a
 * FALLTHROUGH to the data render, so a fifth `ReadState` member — or rerouting
 * paused reads into `unreadable` — drops every surface that does not name the
 * new state into `data ?? []`: zeros, empty tables, and on `PricingInsightsPage`
 * an EmptyState reading "all your pricing is optimal". Measured, not feared: 14
 * of the 62 would have regressed that way, and 8 of those 14 were invisible to a
 * census keyed on `classifyRead` callers because they take a pre-classified
 * `state` from their hook (R126-7's predicted blind spot, D61's scoping lesson).
 * A change to the SENTENCE cannot regress any of them: every surface keeps
 * exactly the arm it already renders, and only the words inside it change.
 */
export function scopePromptI18nKey(gate: ReadGate): string {
    if (isBrowserOffline()) return OFFLINE_NO_DATA_I18N_KEY;
    return gate === 'location' ? SELECT_LOCATION_I18N_KEY : SELECT_ORGANIZATION_I18N_KEY;
}
